import { Routing } from "config/enums/routings";
import type { CreateTrackInterface } from "hooks/useUserTracks";
import type { OpenedTrack } from "./RoutingContext";
import {
  initialRoutingState,
  NonRoutableTrackMetadata,
  RoutingState,
} from "./state";
import { Coords, Track } from "types/app";

export type EditedTrackState = Pick<
  RoutingState,
  | "routing"
  | "trackWaypoints"
  | "computedTrackpoints"
  | "elevations"
  | "trackMeta"
  | "nonRoutableTrackMetadata"
  | "trackColor"
>;

/**
 * The points of the edited track and their data, for saving it - into the opened own track (autosave)
 * or as a new track.
 *
 * A routed track gets its speeds and times from the route (`trackMeta`). A track without routing keeps
 * the recorded data (heart rate, cadence, ...) in `nonRoutableTrackMetadata`, which is kept in sync with
 * the points while they're edited; only the start time and format of the times come from the opened track.
 */
export const getEditedTrackData = (
  state: EditedTrackState,
  openedTrack: OpenedTrack | null
) => {
  const isRoutable = state.routing !== Routing.none;
  const metadata = state.nonRoutableTrackMetadata;
  const openedTrackTime = openedTrack?.track.trackPointsTime;
  // A converted routed track has its route's start, a recorded track the opened track's
  const timeStart = metadata.trackPointsTimeStart ?? openedTrackTime;

  return {
    controlPoints: isRoutable ? state.trackWaypoints : [],
    trackPoints: isRoutable ? state.computedTrackpoints : state.trackWaypoints,
    routing: state.routing,
    trackPointsEle: state.elevations,
    ...(isRoutable
      ? {
          trackPointsSpeed:
            state.trackMeta.trackPointsSpeed ??
            openedTrack?.track.trackPointsSpeed ??
            undefined,
          trackPointsTime:
            state.trackMeta.trackPointsTime ?? openedTrackTime ?? undefined,
        }
      : {
          trackPointsCadence: metadata.trackPointsCadence,
          trackPointsHeartRate: metadata.trackPointsHeartRate,
          trackPointsPower: metadata.trackPointsPower,
          trackPointsSpeed: metadata.trackPointsSpeed,
          trackPointsTemperature: metadata.trackPointsTemperature,
          trackPointsTime:
            timeStart && metadata.trackPointsTimeDatapoints
              ? {
                  startTimestamp: timeStart.startTimestamp,
                  startPointIndex: timeStart.startPointIndex,
                  datapoints: metadata.trackPointsTimeDatapoints,
                }
              : undefined,
        }),
  } satisfies CreateTrackInterface;
};

/**
 * The data of the route as the recorded data of a track without routing, for converting a routed track to one:
 * its points become the route's points, so the route's elevations, speeds and times still fit them.
 * Values that don't have one per point (e.g. the opened track's times of an older route) are left out.
 */
export const getRouteAsRecordedData = (
  state: EditedTrackState,
  openedTrack: OpenedTrack | null
): NonRoutableTrackMetadata => {
  const { trackPointsSpeed, trackPointsTime } = getEditedTrackData(
    state,
    openedTrack
  );
  const pointCount = state.computedTrackpoints.length;
  const perPoint = (values: number[] | null | undefined) =>
    values && values.length === pointCount ? values : undefined;
  const datapoints = perPoint(trackPointsTime?.datapoints);

  return {
    ...initialRoutingState.nonRoutableTrackMetadata,
    trackPointsEle: perPoint(state.elevations) ?? [],
    trackPointsSpeed: perPoint(trackPointsSpeed),
    trackPointsTimeDatapoints: datapoints,
    trackPointsTimeStart:
      datapoints && trackPointsTime
        ? {
            startTimestamp: trackPointsTime.startTimestamp,
            startPointIndex: trackPointsTime.startPointIndex,
          }
        : undefined,
  };
};

/** Data of a new track made of the edited one, for saving it as one of the user's tracks */
export const getNewTrackData = (
  state: EditedTrackState,
  openedTrack: OpenedTrack | null,
  name: string
): CreateTrackInterface => ({
  ...getEditedTrackData(state, openedTrack),
  color: state.trackColor || undefined,
  name,
  links: [],
});

/**
 * The saved coordinates are rounded to 6 decimals (the polyline precision), so they're up to half of this away
 * from the edited ones
 */
const COORD_PRECISION = 1e-6;

const areSamePoints = (edited: Coords[], saved: Coords[]) =>
  edited.length === saved.length &&
  edited.every(
    (point, i) =>
      Math.abs(point[0] - saved[i][0]) < COORD_PRECISION &&
      Math.abs(point[1] - saved[i][1]) < COORD_PRECISION
  );

/**
 * Whether the edited track is the same as the saved one: its points (the control points of a routed track),
 * routing and color. An own track is saved automatically after each change - this is false from the change until
 * it's saved (its route and elevations are loading, or saving it failed).
 */
export const isEditedTrackSaved = (
  state: Pick<RoutingState, "routing" | "trackWaypoints" | "trackColor">,
  savedTrack: Pick<Track, "routing" | "controlPoints" | "trackPoints" | "color">
) =>
  state.routing === savedTrack.routing &&
  // No color picked: the track keeps the saved one (a new track gets a default color when it's saved)
  (!state.trackColor || state.trackColor === savedTrack.color) &&
  areSamePoints(
    state.trackWaypoints,
    (savedTrack.routing === Routing.none
      ? savedTrack.trackPoints
      : savedTrack.controlPoints) ?? []
  );
